/**
 * Cancel / Retry buttons for a company deletion (SF-4813), shared by the
 * Deleted companies tab and the company page's danger zone. Each asks for
 * confirmation first; the caller runs the call and refreshes.
 */
import { Button, Popconfirm, Tag } from 'antd';
import { RedoOutlined, StopOutlined } from '@ant-design/icons';

import { CANCEL_DONE_MESSAGE, type CompanyDeletionStatus } from '../api/companyDeleteClient';

export type DeletionAction = 'cancel' | 'retry';

const COPY: Record<
  DeletionAction,
  { button: string; title: string; description: string; ok: string; no: string }
> = {
  cancel: {
    button: 'Cancel deletion',
    title: 'Cancel this deletion?',
    // Not "people can sign in again": cancel restores the company's own earlier
    // active setting, which may have been off already.
    description: CANCEL_DONE_MESSAGE,
    ok: 'Yes, cancel it',
    no: 'Keep it',
  },
  retry: {
    button: 'Retry deletion',
    title: 'Retry this deletion now?',
    description: 'The deletion starts again straight away and runs in the background.',
    ok: 'Yes, retry',
    no: 'Not now',
  },
};

export interface DeletionActionButtonProps {
  action: DeletionAction;
  /** Shown in the confirmation so staff know which company they are acting on. */
  companyName?: string | null;
  loading?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle';
  onConfirm: () => void;
}

export const DeletionActionButton: React.FC<DeletionActionButtonProps> = ({
  action,
  companyName,
  loading,
  disabled,
  size,
  onConfirm,
}) => {
  const copy = COPY[action];
  return (
    <Popconfirm
      title={copy.title}
      description={companyName ? `${companyName}. ${copy.description}` : copy.description}
      okText={copy.ok}
      cancelText={copy.no}
      onConfirm={onConfirm}
      disabled={disabled || loading}
    >
      <Button
        size={size}
        danger={action === 'retry'}
        icon={action === 'cancel' ? <StopOutlined /> : <RedoOutlined />}
        loading={loading}
        aria-busy={!!loading}
        disabled={disabled}
      >
        {copy.button}
      </Button>
    </Popconfirm>
  );
};

const STATUS_TAGS: Record<CompanyDeletionStatus, { label: string; color: string }> = {
  scheduled: { label: 'Scheduled', color: 'orange' },
  running: { label: 'Deleting', color: 'processing' },
  failed: { label: 'Failed', color: 'red' },
  stopped: { label: 'Stopped', color: 'volcano' },
  done: { label: 'Deleted', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'green' },
};

/** Status (or outcome) of a deletion as a coloured tag. */
export const DeletionStatusTag: React.FC<{ status: CompanyDeletionStatus }> = ({ status }) => {
  const tag = STATUS_TAGS[status] ?? { label: status, color: 'default' };
  return <Tag color={tag.color}>{tag.label}</Tag>;
};
